export interface CameraInfo {
  deviceId: string;
  label: string;
}

/**
 * Owns getUserMedia and everything browser-specific about cameras:
 * back-lens selection, torch, restart after tab switches.
 */
export class CameraManager {
  private stream: MediaStream | null = null;
  readonly video: HTMLVideoElement;

  constructor(video: HTMLVideoElement) {
    this.video = video;
    // iOS Safari requires these to render inline without user gestures.
    this.video.setAttribute("playsinline", "true");
    this.video.muted = true;
  }

  async start(deviceId?: string, opts: { autoZoom?: boolean } = {}): Promise<void> {
    this.stop();
    const constraints: MediaStreamConstraints = {
      audio: false,
      // 2560 ideal: capable phones deliver 1440p+ (sharper downsample for the
      // decode ROI); others clamp to their best mode.
      video: deviceId
        ? { deviceId: { exact: deviceId }, width: { ideal: 2560 }, height: { ideal: 1440 } }
        : { facingMode: { ideal: "environment" }, width: { ideal: 2560 }, height: { ideal: 1440 } },
    };
    this.stream = await navigator.mediaDevices.getUserMedia(constraints);
    this.video.srcObject = this.stream;
    await this.video.play();
    await this.requestContinuousFocus();
    if (opts.autoZoom !== false) {
      await this.applyAutoZoom();
    }
  }

  /**
   * Small printed barcodes fail from lack of px/module, and moving the phone
   * closer just hits the lens's minimum focus distance. Sensor zoom raises
   * px/module without moving: measured 2x zoom doubles density, which per the
   * decode-threshold benchmark moves borderline codes into comfortable reads.
   */
  private async applyAutoZoom(): Promise<void> {
    const range = this.zoomRange;
    if (!range || range.max < 1.5) return;
    try {
      await this.setZoom(Math.min(2, range.max));
    } catch {
      // Zoom is best-effort.
    }
  }

  /** Nudge exposure compensation up in dark scenes. True if applied. */
  async boostExposure(): Promise<boolean> {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return false;
    const caps = track.getCapabilities() as MediaTrackCapabilities & {
      exposureCompensation?: { min: number; max: number; step: number };
    };
    const range = caps.exposureCompensation;
    if (!range || range.max <= 0) return false;
    try {
      await track.applyConstraints({
        advanced: [{ exposureCompensation: Math.min(range.max, range.max * 0.6) } as MediaTrackConstraintSet],
      });
      return true;
    } catch {
      return false;
    }
  }

  get zoomRange(): { min: number; max: number; step: number } | null {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return null;
    const caps = track.getCapabilities() as MediaTrackCapabilities & {
      zoom?: { min: number; max: number; step: number };
    };
    return caps.zoom ?? null;
  }

  async setZoom(zoom: number): Promise<void> {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return;
    await track.applyConstraints({ advanced: [{ zoom } as MediaTrackConstraintSet] });
  }

  /** Barcodes are read close-up; ask for continuous autofocus where supported. */
  private async requestContinuousFocus(): Promise<void> {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return;
    const caps = track.getCapabilities() as MediaTrackCapabilities & { focusMode?: string[] };
    if (caps.focusMode?.includes("continuous")) {
      try {
        await track.applyConstraints({
          advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet],
        });
      } catch {
        // Focus hints are best-effort.
      }
    }
  }

  get resolution(): { width: number; height: number } {
    return { width: this.video.videoWidth, height: this.video.videoHeight };
  }

  stop(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.video.srcObject = null;
  }

  get running(): boolean {
    return this.stream !== null;
  }

  /** Only meaningful after permission was granted (labels are hidden before). */
  async listCameras(): Promise<CameraInfo[]> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((d) => d.kind === "videoinput")
      .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` }));
  }

  get torchSupported(): boolean {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return false;
    const caps = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
    return caps.torch === true;
  }

  async setTorch(on: boolean): Promise<void> {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return;
    await track.applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] });
  }
}
